/**
 * Helpers for grouping and summarizing GIG shifts.
 *
 */

import { GigShiftDay, GigShiftGroup, GigShiftStatus, RawGigShiftDay } from '../types/gigShiftTypes';
import { GigRequestDetail } from '../types/gigApi';
import type { Shift } from '../components/shiftcard/ShiftCard';

/**
 * Map GET /api/gig/requests detail rows (one per Gig_Request_Header +
 * Gig_Request_Detail join) into the RawGigShiftDay shape expected by
 * `groupGigShifts`.
 */
export const mapGigRequestDetailsToRaw = (
    rows: GigRequestDetail[] | undefined | null
): RawGigShiftDay[] => {
    if (!rows || rows.length === 0) return [];
    return rows.map((row) => ({
        requestId: row.requestId,
        requestSeqId: row.requestSeqId,
        skill: row.requestedSkillDesc ?? row.requestedSkillCd ?? undefined,
        skillDesc: row.requestedSkillDesc ?? row.requestedSkillCd ?? undefined,
        team: row.requestedShiftCd ?? undefined,
        date: row.requestDt,
        requestDt: row.requestDt,
        hours: row.requestHours ?? null,
        requestHours: row.requestHours ?? null,
        startTime: row.startTime ?? null,
        endTime: row.endTime ?? null,
        status: (row.requestStatus ?? 'PND') as GigShiftStatus,
        requestStatus: (row.requestStatus ?? 'PND') as GigShiftStatus,
        postedBy: row.requestedByUserNm ?? undefined,
        postedByUserNm: row.requestedByUserNm ?? undefined,
        notes: row.requestDetailNotes ?? row.requestNotes ?? undefined,
        requestNotes: row.requestDetailNotes ?? row.requestNotes ?? undefined,
        rejectionNotes: row.rejectionNotes ?? undefined,
    }));
};

const toDateOnly = (value?: string): string => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (match) return match[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const firstNonEmpty = (...values: Array<string | null | undefined>): string | undefined => {
    for (const value of values) {
        const trimmed = value?.trim();
        if (trimmed) return trimmed;
    }
    return undefined;
};

const normalizeDay = (raw: RawGigShiftDay): GigShiftDay => ({
    requestSeqId: raw.requestSeqId ?? '',
    date: toDateOnly(raw.date ?? raw.requestDt),
    hours: raw.hours ?? raw.requestHours ?? null,
    startTime: raw.startTime ?? null,
    endTime: raw.endTime ?? null,
    skill: firstNonEmpty(raw.skillDesc, raw.skill),
    team: raw.team,
    assigneeName: raw.assigneeName ?? raw.claimedByUserNm,
    status: (raw.status ?? raw.requestStatus ?? 'PND') as GigShiftStatus,
    notes: raw.notes ?? raw.requestNotes,
    rejectionNotes: raw.rejectionNotes,
});

/**
 * Aggregate individual-day statuses into a single group status.
 * - any DEN → DEN
 * - any PND → PND
 * - all APP → APP
 * - all CAN → CAN
 * - otherwise → PST
 */
const aggregateStatus = (days: GigShiftDay[]): GigShiftStatus => {
    if (days.some((d) => d.status === 'DEN')) return 'DEN';
    if (days.some((d) => d.status === 'PND')) return 'PND';
    if (days.every((d) => d.status === 'APP')) return 'APP';
    if (days.every((d) => d.status === 'CAN')) return 'CAN';
    return 'PST';
};

const formatDateLabel = (iso: string): string => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRangeLabel = (first: string, last: string): string => {
    if (!first) return '';
    if (!last || first === last) return formatDateLabel(first);
    const firstD = new Date(first + 'T00:00:00');
    const lastD = new Date(last + 'T00:00:00');
    if (firstD.getFullYear() === lastD.getFullYear()) {
        const firstLabel = firstD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${firstLabel} - ${formatDateLabel(last)}`;
    }
    return `${formatDateLabel(first)} - ${formatDateLabel(last)}`;
};

/**
 * Group raw GIG day rows into GigShiftGroup objects keyed by requestId.
 */
export const groupGigShifts = (rows: RawGigShiftDay[] | undefined | null): GigShiftGroup[] => {
    if (!rows || rows.length === 0) return [];

    const buckets = new Map<string, { raw: RawGigShiftDay; days: GigShiftDay[] }>();
    for (const row of rows) {
        const key = String(row.requestId ?? '');
        if (!key) continue;
        const day = normalizeDay(row);
        if (!buckets.has(key)) {
            buckets.set(key, { raw: row, days: [day] });
        } else {
            buckets.get(key)!.days.push(day);
        }
    }

    const groups: GigShiftGroup[] = [];
    buckets.forEach((bucket, requestId) => {
        const sortedDays = [...bucket.days].sort((a, b) => a.date.localeCompare(b.date));
        const first = sortedDays[0]?.date ?? '';
        const last = sortedDays[sortedDays.length - 1]?.date ?? '';
        const distinctSkills = new Set(
            sortedDays.map((d) => d.skill?.trim()).filter((s): s is string => !!s)
        );
        const title =
            distinctSkills.size > 1
                ? 'Multi-Skill Gig Work'
                : firstNonEmpty(
                    bucket.raw.title,
                    bucket.raw.shiftDesc,
                    bucket.raw.skillDesc,
                    bucket.raw.skill
                  ) ?? 'Gig Work';
        const overallStatus = aggregateStatus(sortedDays);
        const rejectionNotes = overallStatus === 'DEN'
            ? sortedDays.find((d) => d.status === 'DEN' && d.rejectionNotes)?.rejectionNotes
            : undefined;
        groups.push({
            requestId,
            title,
            location: bucket.raw.location ?? bucket.raw.siteName,
            postedBy: bucket.raw.postedBy ?? bucket.raw.postedByUserNm,
            overallStatus,
            totalDays: sortedDays.length,
            dateRangeLabel: formatRangeLabel(first, last),
            firstDate: first,
            lastDate: last,
            notes: bucket.raw.notes ?? bucket.raw.requestNotes,
            rejectionNotes,
            days: sortedDays,
        });
    });

    // Sort groups chronologically by first date.
    groups.sort((a, b) => a.firstDate.localeCompare(b.firstDate));
    return groups;
};

export const mapGigGroupsToShiftCards = (groups: GigShiftGroup[], defaultLocation = ''): Shift[] =>
    groups.map((group) => {
        const firstSeqId = group.days[0]?.requestSeqId ?? '';
        return {
            id: `gig-${group.requestId}-${firstSeqId}`,
            requestId: group.requestId,
            requestSeqId: firstSeqId,
            title: group.title,
            shiftType: 'GIG',
            location: group.location || defaultLocation,
            date: group.firstDate,
            dateRangeLabel: group.dateRangeLabel,
            startTime: '',
            endTime: '',
            rate: '',
            status: group.overallStatus,
            postedBy: group.postedBy,
            claimedBy: group.days.find((day) => day.assigneeName)?.assigneeName,
            notes: group.notes,
            rejectionNotes: group.rejectionNotes,
            isGigShift: true,
            totalDays: group.totalDays,
            gigDays: group.days.map((day) => ({
                requestSeqId: day.requestSeqId,
                date: day.date,
                hours: day.hours,
                status: day.status,
            })),
        };
    });

/**
 * Format a 24-hour "HH:mm" time string as a 12-hour display string, e.g.
 * "13:00" -> "1:00 PM". Returns the original value if it can't be parsed.
 */
export const formatTimeDisplay = (time?: string | null): string => {
    if (!time) return '';
    const match = /^(\d{1,2}):(\d{2})/.exec(time);
    if (!match) return time;
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const suffix = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${hours}:${minutes} ${suffix}`;
};

/**
 * Format a start/end time pair for display, e.g. "8:00 AM - 4:00 PM".
 * Returns an empty string if either time is missing.
 */
export const formatTimeRangeDisplay = (startTime?: string | null, endTime?: string | null): string => {
    if (!startTime || !endTime) return '';
    return `${formatTimeDisplay(startTime)} - ${formatTimeDisplay(endTime)}`;
};

/** A group is editable by a regular user only while it is pending. */
export const isGigGroupEditable = (group: GigShiftGroup): boolean =>
    group.overallStatus === 'PND';

/**
 * A group should only appear on the approve/reject screen while every day
 * within it is still Pending or Cancelled. If any day has already moved to
 * another status (Approved/Denied/Posted), the whole group should only be
 * visible on the calendar, not the approve/reject screen. Groups where every
 * day is Cancelled are also excluded since there is nothing left to action.
 */
export const isGigGroupShowableInApproveReject = (group: GigShiftGroup): boolean =>
    group.days.every((d) => d.status === 'PND' || d.status === 'CAN') &&
    group.days.some((d) => d.status === 'PND');

const toLocalDateOnlyString = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Determine whether a gig group belongs on the given tab (today/thisweek/all),
 * based on whether ANY of its days fall within that range. The group is kept
 * whole (all days included) when it matches; callers should not isolate a
 * single day out of the group.
 */
export const gigGroupMatchesDateFilter = (
    group: GigShiftGroup,
    filter: 'all' | 'today' | 'thisweek'
): boolean => {
    if (filter === 'all') return true;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (filter === 'today') {
        const todayStr = toLocalDateOnlyString(today);
        return group.days.some((d) => d.date === todayStr);
    }

    if (filter === 'thisweek') {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return group.days.some((d) => {
            if (!d.date) return false;
            const dayDate = new Date(`${d.date}T00:00:00`);
            if (Number.isNaN(dayDate.getTime())) return false;
            return dayDate >= startOfWeek && dayDate <= endOfWeek;
        });
    }

    return true;
};
